<script lang="ts">
  import type { BonordBonusContract } from "$lib/types/contracts";
  import type { Teaser } from "$lib/types/shared";
  import Cover from "$lib/Cover.svelte";
  import Lead from "$lib/Lead.svelte";
  import MetaData from "$lib/MetaData.svelte";
  import Picture from "$lib/Picture.svelte";
  import { contentBlockComponents } from "$lib/portableText/utils";
  import { productCategoryMapping } from "$lib/types/products";
  import { breakpoints as br } from "$lib/utils/getBreakpoints";
  import { PortableText } from "@portabletext/svelte";
  import TeaserSection from "$lib/TeaserSection.svelte";
  import sessionValues, { syncSessionValues } from "$lib/sessionValues";
  import TypeformButton from "$lib/TypeformButton.svelte";
  import Button from "$lib/Button.svelte";
  import InputText from "$lib/InputText.svelte";

  const nineDigits = `^[0-9]{9}$`;

  // First parameter is event, second is member number
  const checkMembership = async (_: unknown, number?: string) => {
    if (number) memberNumber = number;
    if (!memberNumber) return;

    loading = true;

    // Sjekk mot api
    const result = await fetch(
      `https://appgw.prod.iktest.co/api-app/pivotal/exists/${memberNumber}`
    );
    if (result.status === 200) {
      const membership_res: { exists: boolean; pivotal_member_card: string } = await result.json();

      if (membership_res.exists) {
        membershipFound = true;
        syncSessionValues(
          {
            mnr: memberNumber,
            mnr_type: "bonord",
          },
          { useBrowserStorage: true }
        );
        // focus summary
        summary?.focus();
      }
    } else if (result.status === 429) {
      fetchFailed = true;
    } else {
      fetchFailed = true;
      membershipFound = false;
    }
    submitted = true;
    loading = false;
  };

  const invalidMessageReset = (e: Event) => {
    membershipFound = false;
    submitted = false;
    const eventTarget = e.currentTarget as HTMLInputElement;
    if (eventTarget) eventTarget.setCustomValidity("");
  };

  const invalidEvent = (e: Event) => {
    const eventTarget = e.currentTarget as HTMLInputElement;
    if (eventTarget?.validity?.valueMissing) {
      eventTarget.setCustomValidity("Kortnummer er påkrevd");
    }
    if (eventTarget?.validity?.patternMismatch) {
      eventTarget.setCustomValidity("Kortnummeret må bestå av 9 siffer");
    }
  };

  interface Props {
    data: {
      bonord_contract: Readonly<BonordBonusContract>;
      app_teaser?: Teaser;
    };
  }

  let { data }: Props = $props();

  let submitted = $state(false);
  let membershipFound = $state(false);
  let loading = $state(false);
  let fetchFailed = $state(false);
  let memberNumber = $state("");
  let summary: HTMLElement | undefined = $state();

  let app_teaser = $derived(data?.app_teaser);
  let contract = $derived(data?.bonord_contract?.contract);
  let hero = $derived(data?.bonord_contract?.hero);
  let productOffers = $derived(data?.bonord_contract?.product_offers);
  let products = $derived(
    data?.bonord_contract?.products?.map((x) => ({
      title: x.teaser.title,
      lead: x.teaser.description,
      image: x.teaser.image,
      buttonText: x.teaser.button,
      categories: x.product_categories,
      slug: `kategori/${
        productCategoryMapping.find((cat) => x.product_categories.includes(cat.key))?.key || ""
      }`,
    })) ?? []
  );

  $effect(() => {
    if ($sessionValues["epi"] && submitted === false) {
      checkMembership(null, $sessionValues["epi"]);
    }
  });
</script>

<MetaData
  data={{
    title: `${contract?.name} – ${hero}`,
    description: contract.description,
  }}
/>

<Cover class="mt-2 mb-8">
  <div
    class="pt-[calc(5*var(--text-h-hero))] pb-8 laptop:pr-[calc(12.5vw-var(--w-cover-x))] text-white"
  >
    <h1 class="my-0 text-h-hero font-title tablet:leading-snug">
      {hero}
    </h1>
  </div>

  {#snippet background()}
    <div>
      {#if contract?.image?.src}
        <Picture
          src={contract.image.src}
          alt={contract.image.alt}
          srcset={contract.image.srcset}
          srcsetWebp={contract.image.srcsetWebp}
          sizes={`(min-width: ${br.desktop}) 85vw, (min-width: ${br.laptop}) 90vw, 100vw`}
          width={contract.image?.asset?.metadata?.dimensions?.width}
          height={contract.image?.asset?.metadata?.dimensions?.height}
          class="h-full w-full object-cover laptop:rounded-2xl"
          loading="eager"
        />
      {/if}
    </div>
  {/snippet}
</Cover>

<h2 class="text-h1 mx-edge-x laptop:pl-cover-x">{contract.name}</h2>
<Lead class="max-w-prose mx-edge-x laptop:pl-cover-x">{contract.description}</Lead>

<div class="text-body mx-edge-x laptop:pl-cover-x max-w-prose">
  <PortableText
    value={contract.content}
    components={contentBlockComponents}
    onMissingComponent={false}
  />
</div>

<div
  class="children-max-w-prose pl-edge-x mr-edge-x pr-3 landscape:pr-[8vw] py-8 my-12 rounded-[0_1rem_1rem_0] text-body"
  class:bg-$aurora={!membershipFound}
  class:bg-$reinlav={membershipFound}
>
  <section class="laptop:pl-cover-x">
    <h3>Ditt Bonord-medlemskap</h3>

    <!-- <p>Med Bonord-medlemskapet ditt verifisert hos Ishavskraft får du tilgang til:</p> -->
    <!-- <ul> -->
    <!--   <li>Egen strømavtale</li> -->
    <!--   <li>Rabatt på utvalgte produkter (se nedenfor)</li> -->
    <!-- </ul> -->

    <details open={!membershipFound}>
      <summary bind:this={summary}>
        <strong>Ditt medlemskortnummer</strong>
        {#if membershipFound}
          {memberNumber}
        {/if}
      </summary>
      <form
        onsubmit={(e) => {
          e.preventDefault();
          checkMembership(e);
        }}
        id="bonord-form"
        class:submitted
        aria-live="polite"
      >
        <p>
          <label for="mnr">
            <strong>Bonord medlemskortnummer, 9 siffer</strong>
            <small class="block"
              >Ditt medlemskortnummer finner du på Min Side på
              <a href="https://www.bonord.no/">bonord.no</a>
              og i appen til Bonord, under kortnummer.
              <em>Obs! Ikke medlemsnummer</em>.</small
            >
          </label>
        </p>

        <p class="flex flex-wrap gap-2">
          <InputText
            bind:value={memberNumber}
            id="mnr"
            name="mnr"
            type="text"
            pattern={nineDigits}
            required
            placeholder="Kortnummer"
            oninvalid={invalidEvent}
            oninput={invalidMessageReset}
          />

          <button class="button">Verifiser</button>
        </p>

        {#if loading}
          <p>Henter medlemsinfo fra Bonord...</p>
        {/if}
      </form>
    </details>

    <div aria-live="polite">
      {#if !loading && submitted && !membershipFound}
        <p class="shadow-[inset_0_0_0_3px_var(--uer)] p-4 rounded-2xl">
          <strong>Vi fant deg dessverre ikke hos Bonord.</strong> Har du skrevet inn riktig medlemskortnummer?
          Prøv gjerne på nytt, eller ta kontakt med oss i Ishavskraft på 776 12 000.
        </p>
      {/if}

      {#if submitted && !loading && fetchFailed}
        <p class="shadow-[inset_0_0_0_3px_var(--uer)] p-4 rounded-2xl">
          <em>Det skjedde en feil, vennligst prøv igjen.</em>
        </p>
      {/if}

      {#if submitted && membershipFound && !loading}
        <p><em>Ditt medlemsskap er verifisert.</em></p>
      {/if}

      {#if membershipFound}
        <hr class="my-6" />
        <p class="text-h2">
          <TypeformButton
            class="button-primary"
            url="//form.typeform.com/to/euUF2JJe"
            customParameters={{ produkt: "Bonord strøm", product_id: contract.product_id || "" }}
            arrow
          >
            Bestill strømavtale
          </TypeformButton>
        </p>
      {/if}
    </div>

    {#if !loading && submitted && (fetchFailed || !membershipFound)}
      <hr />
    {/if}

    {#if !membershipFound}
      <p>
        <strong>Verifiser</strong> ditt Bonord-medlemskap for fordelene hos Ishavskraft.
      </p>
    {/if}
  </section>
</div>

<div class="mx-edge-x text-body my-12">
  <div class="laptop:ml-cover-x">
    <PortableText
      value={productOffers}
      components={contentBlockComponents}
      onMissingComponent={false}
    />
  </div>

  {#each products as p (p.slug)}
    <div class="responsive-wrapper blue-box laptop:ml-cover-x">
      {#if p.image && p.image.src}
        <Picture
          src={p.image.src}
          alt={p.image.alt}
          srcset={p.image.srcset}
          srcsetWebp={p.image.srcsetWebp}
          sizes={`(min-width: ${br.desktop}) 85vw, (min-width: ${br.laptop}) 90vw, 100vw`}
          width={p.image?.asset?.metadata?.dimensions?.width}
          height={p.image?.asset?.metadata?.dimensions?.height}
          class="object-cover w-full tablet:w-35% tablet:h-initial"
          --picture-aspect-ratio="16/9"
        />
      {/if}
      <div class="px-4 my-6 flex-1">
        <h2>{p.title}</h2>
        <p>{p.lead}</p>
        <div class="mt-6">
          <p>
            <Button
              class="button {membershipFound ? 'button-success' : ''}"
              href={`/nettbutikk/${p.slug}`}
              rel="nofollow"
              arrow
            >
              Vårt utvalg<span class="sr-only">: {p.title}</span>
            </Button>
          </p>
        </div>
      </div>
    </div>
  {/each}

  <div class="my-3 children-my-1 text-end pl-inline text-base">
    <PortableText
      value={contract.finePrint}
      components={contentBlockComponents}
      onMissingComponent={false}
    />
  </div>
  {#if contract.documents && contract.documents.length > 0}
    <ul class="my-8">
      {#each contract.documents as doc (doc)}
        <li>
          <a href={doc.url} target="_blank">{doc.title ?? "Fil uten navn"}</a>
        </li>
      {/each}
    </ul>
  {/if}
</div>

{#if app_teaser}
  <TeaserSection class="my-20" circleImage={true} teaser={app_teaser} href="/app" />
{/if}

<style>
  .blue-box {
    background-color: var(--primary-white);
    border-radius: 1rem;
    margin-block: var(--s-8);
  }

  .blue-box :global(img) {
    border-radius: 1rem 1rem 0 0;
  }

  .responsive-wrapper {
    display: flex;
    flex-direction: column;
  }

  @media (--tablet) {
    .blue-box {
      padding-inline-end: 2vw;
    }
    .blue-box :global(img) {
      border-radius: 1rem 0 0 1rem;
    }
    .responsive-wrapper {
      flex-direction: row;
      gap: 2rem;
    }
  }
</style>
